import './style/salesProductDetails.scss';

import {AwesomeIcon, CloseButton, stopPropagation} from '@dvrd/dvr-controls';

import classNames from 'classnames';

import React, {ForwardedRef, forwardRef, useImperativeHandle, useRef, useState} from 'react';

import {PeriodLabel, SalesProductView} from '../../../util/enums';

import {ActivateHandle} from '../../../util/interfaces';

import {GeneralSalesProduct} from '../../../util/types';

import SalesProductStatsController from './salesProductStats/salesProductStatsController';

import IDate from '@dvrd/idate';

import EditSalesProductController from './edit/editSalesProductController';

interface Props {
    onReload: VoidFunction;
}

function SalesProductDetailsController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {onReload} = props;
    const [view, setView] = useState<SalesProductView>(SalesProductView.STATS);
    const [active, setActive] = useState(false);
    const product = useRef<GeneralSalesProduct | null>(null);
    const accountIDs = useRef<Set<string>>(new Set());
    const period = useRef<PeriodLabel>(PeriodLabel.LAST_30_DAYS);
    const customPeriodStart = useRef<IDate | null>(null);
    const customPeriodEnd = useRef<IDate | null>(null);

    function onActivate(
        _product: GeneralSalesProduct,
        _accountIDs: Set<string>,
        _period: PeriodLabel,
        customStart: IDate | null,
        customEnd: IDate | null
    ) {
        product.current = _product;
        accountIDs.current = _accountIDs;
        period.current = _period;
        customPeriodStart.current = customStart;
        customPeriodEnd.current = customEnd;
        setActive(true);
    }

    function onClose() {
        setActive(false);
        setView(SalesProductView.STATS);
    }

    function onChangeView(view: SalesProductView) {
        return function () {
            setView(view);
        };
    }

    function onEditSuccess() {
        onReload();
    }

    function onCloseEdit() {
        setView(SalesProductView.STATS);
    }

    function renderHeader() {
        const _product = product.current;
        if (!_product) return null;
        const imageURL = _product.image_url;
        const title = _product.title_;
        const ean = _product.ean;
        return (
            <div className='product-header'>
                {!imageURL ? (
                    <AwesomeIcon name='image' className='image-icon' />
                ) : (
                    <img src={imageURL} alt={title ?? ''} className='product-image' />
                )}
                <div className='product-identifiers'>
                    <label className='product-title'>{title ?? ''}</label>
                    <label className='ean'>EAN: {ean}</label>
                </div>
                <CloseButton onClick={onClose} className='btn-close' />
            </div>
        );
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <>
            <div className={classNames('sales-product-details-background', active && 'active')} onClick={onClose} />
            <div className={classNames('sales-product-details', active && 'active')} onClick={stopPropagation}>
                {renderHeader()}
                {view === SalesProductView.STATS && (
                    <SalesProductStatsController
                        onClose={onClose}
                        product={product.current}
                        accountIDs={accountIDs.current}
                        onChangeView={onChangeView}
                        period={period.current}
                        customPeriodStart={customPeriodStart.current}
                        customPeriodEnd={customPeriodEnd.current}
                    />
                )}
                {view === SalesProductView.EDIT_PRODUCT && (
                    <EditSalesProductController
                        onEditSuccess={onEditSuccess}
                        onClose={onCloseEdit}
                        product={product.current}
                        accountIDs={accountIDs.current}
                    />
                )}
            </div>
        </>
    );
}

SalesProductDetailsController.displayName = 'SalesProductDetailsController';

export default forwardRef<ActivateHandle, Props>(SalesProductDetailsController);
